/**
 * Response Step Subcomponents (MA-FE-079)
 * 
 * Modular components for the unified Response Step editor:
 * - ResponseMessageEditor: Title and message content
 * - ResponseOptionsEditor: Response options with per-option routing
 * - ResponseRoutingEditor: Default routing and terminal exit settings
 * 
 * These components enforce explicit routing and eliminate implicit behavior.
 */

import React from 'react';
import { 
  Plus, 
  Trash2, 
  Star, 
  GripVertical, 
  GitBranch, 
  LogOut,
  AlertCircle,
  ArrowRight,
  CheckCircle2,
  XCircle,
} from 'lucide-react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Separator } from '@/components/ui/separator';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Alert,
  AlertDescription,
} from '@/components/ui/alert';
import { cn } from '@/lib/utils';

// =============================================================================
// Route Target Selector (Shared Component)
// =============================================================================

export function RouteTargetSelector({ 
  value, 
  onChange, 
  nodes = [], 
  currentNodeId, 
  className,
  required = false,
  label = "Routes to:",
  showLabel = true,
}) {
  // Filter out current node and get valid targets
  const availableNodes = nodes.filter(n => n.id !== currentNodeId);
  
  // MA-FUNNEL-ROUTING-052: No exit aliases. Group terminal nodes for clarity.
  const terminalNodes = availableNodes.filter(n => n.content?.is_terminal);
  const regularNodes = availableNodes.filter(n => !n.content?.is_terminal);
  
  const isStaleAlias = value?.startsWith("__exit_");
  const isValid = !!value || !required;
  
  return (
    <div className={cn("flex items-center gap-2", className)}>
      {showLabel && (
        <span className="text-xs text-slate-200/60 whitespace-nowrap flex items-center gap-1">
          {label}
          {required && <span className="text-red-400">*</span>}
        </span>
      )}
      <Select value={value || ""} onValueChange={onChange}>
        <SelectTrigger 
          className={cn(
            "h-8 text-sm bg-white/5 text-slate-100 ring-1",
            !isValid && required ? "ring-red-500/50" : "ring-white/10"
          )}
        >
          <SelectValue placeholder="Select destination..." />
        </SelectTrigger>
        <SelectContent>
          {/* Terminal Steps group */}
          {terminalNodes.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-slate-400">
                Terminal Steps
              </div>
              {terminalNodes.map(node => {
                const outcome = node.content?.outcome;
                const isQualified = outcome === "qualified";
                return (
                  <SelectItem key={node.id} value={node.id}>
                    <div className={cn("flex items-center gap-2", isQualified ? "text-green-400" : "text-red-400")}>
                      {isQualified ? <CheckCircle2 className="h-3.5 w-3.5" /> : <XCircle className="h-3.5 w-3.5" />}
                      <span>{node.metadata?.label || node.id}</span>
                    </div>
                  </SelectItem>
                );
              })}
            </>
          )}
          
          {regularNodes.length > 0 && (
            <>
              <div className="px-2 py-1.5 text-xs font-medium text-slate-400 border-t border-white/10 mt-1">
                Steps
              </div>
              {regularNodes.map(node => (
                <SelectItem key={node.id} value={node.id}>
                  {node.metadata?.label || node.id}
                </SelectItem>
              ))}
            </>
          )}
          
          {/* Stale alias warning */}
          {isStaleAlias && (
            <div className="px-2 py-1.5 text-xs text-amber-400 border-t border-white/10 mt-1">
              ⚠ Current value is a stale alias — re-assign to a real step
            </div>
          )}
        </SelectContent>
      </Select>
    </div>
  );
}

// =============================================================================
// Response Message Editor (MA-FE-079)
// =============================================================================

export function ResponseMessageEditor({ 
  content, 
  onUpdate, 
  isTerminal = false 
}) {
  const updateField = (field, value) => {
    onUpdate({ [field]: value });
  };
  
  return (
    <div className="space-y-4">
      {/* Terminal Exit Banner */}
      {isTerminal && (
        <div className={cn(
          "flex items-start gap-3 p-3 rounded-lg border",
          content.outcome === "qualified" ? "bg-green-500/10 border-green-500/20" :
          content.outcome === "not_qualified" ? "bg-red-500/10 border-red-500/20" :
          content.outcome === "abandoned" ? "bg-amber-500/10 border-amber-500/20" :
          content.outcome === "no_consent" ? "bg-orange-500/10 border-orange-500/20" :
          content.outcome === "error" ? "bg-slate-500/10 border-slate-500/20" :
          "bg-blue-500/10 border-blue-500/20"
        )} data-testid="terminal-exit-banner">
          <LogOut className={cn(
            "h-5 w-5 flex-shrink-0 mt-0.5",
            content.outcome === "qualified" ? "text-green-500" :
            content.outcome === "not_qualified" ? "text-red-400" :
            content.outcome === "abandoned" ? "text-amber-400" :
            content.outcome === "no_consent" ? "text-orange-400" :
            content.outcome === "error" ? "text-slate-400" :
            "text-blue-400"
          )} />
          <div className="text-sm">
            <p className={cn(
              "font-medium",
              content.outcome === "qualified" ? "text-green-500" :
              content.outcome === "not_qualified" ? "text-red-400" :
              content.outcome === "abandoned" ? "text-amber-400" :
              content.outcome === "no_consent" ? "text-orange-400" :
              content.outcome === "error" ? "text-slate-400" :
              "text-blue-400"
            )}>
              Terminal Exit Point
            </p>
            <p className="text-slate-200/60">
              {content.outcome === "qualified" ? "User completes funnel as qualified lead" :
               content.outcome === "not_qualified" ? "User exits as not qualified" :
               content.outcome === "abandoned" ? "User abandoned before completion" :
               content.outcome === "no_consent" ? "User declined consent" :
               content.outcome === "error" ? "Error/failure terminal state" :
               "Conversation ends at this step"
              }
            </p>
          </div>
        </div>
      )}
      
      {/* Title (optional) */}
      <div className="space-y-2">
        <Label htmlFor="response-title" className="text-sm text-slate-200/80">
          Title (optional)
        </Label>
        <Input
          id="response-title"
          value={content.title || ""}
          onChange={(e) => updateField('title', e.target.value)}
          placeholder="Optional headline..."
          className="bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
        />
      </div>
      
      {/* Message (required) */}
      <div className="space-y-2">
        <Label htmlFor="response-text" className="text-sm text-slate-200/80">
          Message <span className="text-red-400">*</span>
        </Label>
        <Textarea
          id="response-text"
          value={content.message || ""}
          onChange={(e) => updateField('message', e.target.value)}
          placeholder="Enter your message..."
          className={cn(
            "min-h-[100px] bg-white/5 text-slate-100 ring-1 placeholder:text-slate-400/70",
            !content.message ? "ring-red-500/30" : "ring-white/10"
          )}
          data-testid="response-text-input"
        />
        {!content.message && (
          <p className="text-xs text-red-400">Message is required</p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Response Options Editor (MA-FE-079)
// =============================================================================

export function ResponseOptionsEditor({
  responses = [],
  edges = [],
  uiConfig = {},
  allNodes = [],
  currentNodeId,
  onUpdateResponses,
  onUpdateEdges,
  onUpdateUIConfig,
  onSetResponseRoute,  // MA-ARCH-020B: Use mutation API for routing changes (if available)
  isTerminal = false,
}) {
  const hasResponses = responses.length > 0;
  
  // Get the route target for a specific response option
  const getRouteForOption = (optionId, optionValue) => {
    // First check response-level target (authoritative)
    const response = responses.find(r => r.id === optionId);
    if (response?.next_step_id || response?.target) {
      return response.next_step_id || response.target;
    }
    // Fall back to edge lookup
    const edge = edges.find(e => 
      e.condition?.includes(optionId) || 
      e.condition?.includes(`'${optionValue}'`) ||
      e.condition?.includes(`"${optionValue}"`) ||
      e.response_id === optionId
    );
    return edge?.target_node_id || null;  // MA-FUNNEL-ROUTING-050A: null = unrouted
  };
  
  // Update route for a specific response option
  // MA-ARCH-020B: Update BOTH the response's target field AND the edges
  // This ensures the normalizer can create edges from response fields
  const updateRouteForOption = (optionId, optionValue, targetNodeId) => {
    // If mutation API is available, use it (persists to DB)
    if (onSetResponseRoute && currentNodeId) {
      onSetResponseRoute(currentNodeId, optionId, targetNodeId);
      return;
    }
    
    // Otherwise, update response.next_step_id directly so normalizer can read it
    // This is the KEY FIX: the response field is the source of truth for normalization
    const updatedResponses = responses.map(opt => {
      if (opt.id === optionId) {
        return {
          ...opt,
          next_step_id: targetNodeId,  // MA-ARCH-020B: This is what normalizer reads
          target: targetNodeId,  // Legacy field for compatibility
        };
      }
      return opt;
    });
    onUpdateResponses(updatedResponses);
    
    // Also update edges for local UI state
    const newEdges = responses.map(opt => {
      if (opt.id === optionId) {
        return {
          target_node_id: targetNodeId,
          condition: `response.value == '${opt.value || optionValue}'`,
          response_id: optionId,
        };
      }
      const existingEdge = edges.find(e => 
        e.condition?.includes(opt.id) ||
        e.condition?.includes(`'${opt.value}'`) ||
        e.response_id === opt.id
      );
      return existingEdge || {
        target_node_id: null,
        condition: `response.value == '${opt.value}'`,
        response_id: opt.id,
      };
    });
    
    onUpdateEdges(newEdges);
  };
  
  const updateResponse = (index, updates) => {
    const newResponses = [...responses];
    const oldValue = newResponses[index].value;
    newResponses[index] = { ...newResponses[index], ...updates };
    onUpdateResponses(newResponses);
    
    // Update edge conditions if value changed
    if (updates.value && updates.value !== oldValue) {
      const newEdges = edges.map(edge => {
        if (edge.condition?.includes(`'${oldValue}'`) || edge.condition?.includes(`"${oldValue}"`)) {
          return {
            ...edge,
            condition: edge.condition.replace(oldValue, updates.value)
          };
        }
        return edge;
      });
      onUpdateEdges(newEdges);
    }
  };
  
  const addResponse = () => {
    const newId = `opt_${Date.now().toString(36)}`;
    const newValue = `option_${responses.length + 1}`;
    const newResponses = [
      ...responses,
      { 
        id: newId, 
        label: `Option ${responses.length + 1}`, 
        value: newValue,
        acknowledgement: "Great! Let's continue.",
        next_step_id: null  // MA-FUNNEL-ROUTING-050A: Unrouted until user sets destination
      },
    ];
    
    // MA-BE-070-HARDENING: Create edge with response_id for canonical matching
    const newEdges = [
      ...edges.filter(e => e.condition || e.response_id),
      { 
        target_node_id: null, 
        response_id: newId,  // Canonical identifier
        source_handle: newId,  // For UI compatibility
        type: "response",
        condition: `response.id == '${newId}'`  // Fallback matching
      }
    ];
    
    onUpdateResponses(newResponses);
    onUpdateEdges(newEdges);
  };
  
  const removeResponse = (index) => {
    const removedOption = responses[index];
    const newResponses = responses.filter((_, i) => i !== index);
    
    const newEdges = edges.filter(e => 
      !e.condition?.includes(removedOption.value) &&
      !e.condition?.includes(removedOption.id)
    );
    
    onUpdateResponses(newResponses);
    onUpdateEdges(newEdges);
    
    // Clear recommended if removed
    if (uiConfig.recommended_option_id === removedOption.id) {
      onUpdateUIConfig({ recommended_option_id: null });
    }
  };
  
  const setRecommended = (optionId) => {
    onUpdateUIConfig({
      recommended_option_id: uiConfig.recommended_option_id === optionId ? null : optionId,
    });
  };
  
  const hasBranching = edges.some(e => e.target_node_id && !e.target_node_id.startsWith("__"));
  
  if (isTerminal) {
    return null;
  }
  
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Label className="text-sm text-slate-200/80">Response Options</Label>
          {!hasResponses && (
            <Badge className="text-xs bg-slate-500/20 text-slate-300 ring-1 ring-slate-400/20">
              Message Only
            </Badge>
          )}
          {hasResponses && hasBranching && (
            <Badge className="gap-1 text-xs bg-blue-500/20 text-blue-200 ring-1 ring-blue-400/20">
              <GitBranch className="h-3 w-3" />
              Branching
            </Badge>
          )}
        </div>
        <Button variant="outline" size="sm" onClick={addResponse}>
          <Plus className="h-4 w-4 mr-1" />
          Add Response
        </Button>
      </div>
      
      {!hasResponses && (
        <p className="text-xs text-slate-200/50 italic">
          No response options. Configure routing below.
        </p>
      )}
      
      <div className="space-y-3">
        {responses.map((option, index) => {
          const routeTarget = getRouteForOption(option.id, option.value);
          
          return (
            <div
              key={option.id}
              className="p-3 rounded-lg bg-white/5 border border-white/10 space-y-2"
              data-testid={`response-option-${index}`}
            >
              {/* Option label and value */}
              <div className="flex items-center gap-2">
                <GripVertical className="h-4 w-4 text-slate-200/30 flex-shrink-0" />
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <Input
                    value={option.label || ""}
                    onChange={(e) => updateResponse(index, { label: e.target.value })}
                    placeholder="Label"
                    className="h-8 text-sm bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
                  />
                  <Input
                    value={option.value || ""}
                    onChange={(e) => updateResponse(index, { value: e.target.value })}
                    placeholder="Value"
                    className="h-8 text-sm bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
                  />
                </div>
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    "h-8 w-8",
                    uiConfig.recommended_option_id === option.id
                      ? "text-amber-400"
                      : "text-slate-200/30 hover:text-amber-400"
                  )}
                  onClick={() => setRecommended(option.id)}
                  title="Set as recommended"
                >
                  <Star className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-slate-200/30 hover:text-destructive"
                  onClick={() => removeResponse(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
              
              {/* Route selector */}
              <div className="pl-6">
                <RouteTargetSelector
                  value={routeTarget}
                  onChange={(target) => updateRouteForOption(option.id, option.value, target)}
                  nodes={allNodes}
                  currentNodeId={currentNodeId}
                />
              </div>
              
              {/* Per-option acknowledgement */}
              <div className="pl-6">
                <Input
                  value={option.acknowledgement || ""}
                  onChange={(e) => updateResponse(index, { acknowledgement: e.target.value })}
                  placeholder="Acknowledgement (e.g., Great choice!)"
                  className="h-8 text-sm bg-white/5 text-slate-100 ring-1 ring-white/10 placeholder:text-slate-400/70"
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// =============================================================================
// Response Routing Editor (MA-FE-079)
// ALWAYS visible - shows per-response routing AND default/fallback routing
// =============================================================================

export function ResponseRoutingEditor({
  content,
  responses = [],
  defaultNextStepId,
  isTerminal,
  allNodes = [],
  currentNodeId,
  onUpdateContent,
  onUpdateDefaultRoute,
  onToggleTerminal,
}) {
  const hasResponses = responses.length > 0;
  const needsDefaultRoute = !hasResponses && !isTerminal;
  const hasValidRoute = !!defaultNextStepId;
  
  // MA-FE-079: Validation - message-only steps REQUIRE explicit routing
  const isRoutingValid = isTerminal || hasResponses || hasValidRoute;
  
  return (
    <div className="space-y-4">
      {/* Section Header - Routing Configuration */}
      <div className="flex items-center gap-2">
        <ArrowRight className="h-4 w-4 text-indigo-400" />
        <Label className="text-sm font-medium text-slate-200">Routing Configuration</Label>
      </div>
      
      {/* MA-FE-079: Explicit Default Routing - ALWAYS visible when not terminal */}
      {!isTerminal && (
        <div className="space-y-3 p-3 rounded-lg bg-white/5 border border-white/10">
          <div className="flex items-center justify-between">
            <div>
              <Label className="text-sm text-slate-200/80 flex items-center gap-2">
                {hasResponses ? "Default Fallback Route" : "Continue to Step"}
                {!hasResponses && <span className="text-red-400">*</span>}
              </Label>
              <p className="text-xs text-slate-200/60">
                {hasResponses 
                  ? "Used when no response option is matched (optional fallback)"
                  : "Required: Where should users go after this message?"
                }
              </p>
            </div>
          </div>
          
          <RouteTargetSelector
            value={defaultNextStepId}
            onChange={onUpdateDefaultRoute}
            nodes={allNodes}
            currentNodeId={currentNodeId}
            required={!hasResponses}
            showLabel={false}
            className="w-full"
          />
          
          {/* Validation error for message-only steps without routing */}
          {needsDefaultRoute && !hasValidRoute && (
            <Alert className="border-red-500/30 bg-red-500/10">
              <AlertCircle className="h-4 w-4 text-red-400" />
              <AlertDescription className="text-red-200 text-sm">
                You must select a destination step. This step has no response options, 
                so explicit routing is required to proceed.
              </AlertDescription>
            </Alert>
          )}
          
          {/* Info for steps with responses */}
          {hasResponses && (
            <p className="text-xs text-slate-200/40 italic">
              Per-response routing is configured above. This fallback is used if no response matches.
            </p>
          )}
        </div>
      )}
      
      <Separator className="bg-white/10" />
      
      {/* Terminal/Exit Settings */}
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm text-slate-200/80 flex items-center gap-2">
              <LogOut className="h-4 w-4" />
              Terminal Exit
            </Label>
            <p className="text-xs text-slate-200/60">
              Marks this as an end point that exits the funnel
            </p>
          </div>
          <Switch
            checked={isTerminal}
            onCheckedChange={onToggleTerminal}
            data-testid="terminal-toggle"
          />
        </div>
        
        {isTerminal && (
          <div className="space-y-2 pl-6 border-l-2 border-white/10">
            <Label htmlFor="outcome" className="text-sm text-slate-200/80">
              Exit Outcome <span className="text-red-400">*</span>
            </Label>
            <Select
              value={content.outcome || "qualified"}
              onValueChange={(value) => onUpdateContent({ outcome: value })}
              data-testid="exit-outcome-select"
            >
              <SelectTrigger id="outcome" className="bg-white/5 text-slate-100 ring-1 ring-white/10" data-testid="exit-outcome-trigger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="qualified" data-testid="outcome-qualified">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-500" />
                    Qualified
                  </div>
                </SelectItem>
                <SelectItem value="not_qualified" data-testid="outcome-not-qualified">
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-400" />
                    Not Qualified
                  </div>
                </SelectItem>
                <SelectItem value="abandoned" data-testid="outcome-abandoned">
                  <div className="flex items-center gap-2">
                    <LogOut className="h-4 w-4 text-amber-400" />
                    Abandoned
                  </div>
                </SelectItem>
                <SelectItem value="no_consent" data-testid="outcome-no-consent">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-4 w-4 text-orange-400" />
                    No Consent
                  </div>
                </SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-slate-200/60">
              Determines how this exit is tracked in analytics
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Response Step Validation (MA-FE-079)
// Enforces explicit routing rules - blocks publishing if invalid
// =============================================================================

export function validateResponseStep(node) {
  const content = node.content || {};
  const responses = content.responses || [];
  const isTerminal = content.is_terminal || false;
  const defaultNextStepId = content.default_next_step_id;
  const edges = node.edges || [];
  
  const errors = [];
  const warnings = [];
  
  // Message is required
  if (!content.message || content.message.trim() === '') {
    errors.push({
      field: 'message',
      message: 'Message is required',
      severity: 'error'
    });
  }
  
  // MA-FE-079: Routing validation rules
  // Rule 1: If NO response options AND NOT terminal → default_next_step_id is REQUIRED
  if (responses.length === 0 && !isTerminal) {
    // Check for explicit default routing
    const hasExplicitDefault = !!defaultNextStepId;
    
    // Also check for edge-based default
    const defaultEdge = edges.find(e => !e.condition && e.target_node_id);
    const hasEdgeDefault = !!defaultEdge;
    
    if (!hasExplicitDefault && !hasEdgeDefault) {
      errors.push({
        field: 'default_next_step_id',
        message: 'Message-only steps require explicit routing. Select a destination step or mark as terminal.',
        severity: 'error',
        blocking: true  // Blocks publishing
      });
    }
  }
  
  // Rule 2: If response options exist, each should have routing (warning, not blocking)
  if (responses.length > 0 && !isTerminal) {
    const responsesWithoutRouting = responses.filter(r => {
      // Check if this response has a matching edge
      const hasEdge = edges.some(e => 
        e.condition?.includes(r.id) || 
        e.condition?.includes(`'${r.value}'`) ||
        e.source_handle === r.id
      );
      return !hasEdge && !r.target;
    });
    
    if (responsesWithoutRouting.length > 0 && !defaultNextStepId) {
      warnings.push({
        field: 'responses',
        message: `${responsesWithoutRouting.length} response(s) have no explicit routing and no fallback is set.`,
        severity: 'warning'
      });
    }
  }
  
  // Rule 3: Terminal nodes should not have routing configured (warning)
  if (isTerminal && (defaultNextStepId || edges.length > 0)) {
    warnings.push({
      field: 'terminal',
      message: 'Terminal steps should not have routing configured. Routing will be ignored.',
      severity: 'warning'
    });
  }
  
  return {
    isValid: errors.filter(e => e.severity === 'error').length === 0,
    canPublish: errors.filter(e => e.blocking).length === 0,
    errors,
    warnings
  };
}

// =============================================================================
// Legacy Type Normalizer (MA-FE-079)
// =============================================================================

export function normalizeNodeType(node) {
  if (!node) return node;
  
  // MA-FE-079: Convert legacy types to "response"
  if (node.node_type === 'message' || node.node_type === 'question') {
    return {
      ...node,
      node_type: 'response',
      // Preserve original type for debugging
      _legacy_type: node.node_type
    };
  }
  
  return node;
}

export function normalizeFunnelNodes(nodes) {
  if (!Array.isArray(nodes)) return nodes;
  return nodes.map(normalizeNodeType);
}
